import { websocketService, type PublicRoom } from './websocketService';
// Read through a leaf module rather than touching `import.meta` here — see
// apiBase.ts. It is the difference between this file being testable and not.
import { API_BASE } from './apiBase';

export type { PublicRoom };

/**
 * THE LIVE LIST OF GAMES YOU CAN ACTUALLY WALK INTO.
 *
 * Two sources, one list, and they are NOT redundant:
 *
 *   - `GET /api/rooms/public` seeds it on mount, so the section is populated on
 *     the first frame instead of sitting empty until a socket happens to tick.
 *     An empty list has to MEAN "there are no games", and it can't mean that if
 *     it also means "we haven't heard yet".
 *   - `public_rooms_updated` keeps it live from then on. The server debounces it
 *     200ms and emits to every socket on any create / join / leave / visibility
 *     change, so a room a host opens mid-lobby appears here without a refresh.
 *
 * Do NOT source this from `GET /api/games/public`. That endpoint is FINISHED
 * game records — it would offer people games that are already over.
 */

/** One-shot seed. Never throws; an unreachable API is an empty list, not a
    broken screen. The socket will correct us within 200ms of anything real. */
export async function fetchPublicRooms(): Promise<PublicRoom[]> {
  if (!API_BASE) return [];
  try {
    const res = await fetch(`${API_BASE}/rooms/public`);
    if (!res.ok) return [];
    const rooms = await res.json();
    return Array.isArray(rooms) ? rooms : [];
  } catch {
    return [];
  }
}

/**
 * Seed once, then stay live. Returns an unsubscribe.
 *
 * The seed is raced against the socket deliberately: if a `public_rooms_updated`
 * lands while the fetch is still in flight, the socket wins and the stale seed
 * is dropped on arrival. The socket is authoritative; the REST call only exists
 * to fill the gap before the first tick.
 */
export function subscribePublicRooms(
  onRooms: (rooms: PublicRoom[]) => void,
): () => void {
  let cancelled = false;
  let sawLiveUpdate = false;

  const onUpdate = (data: any) => {
    if (cancelled) return;
    sawLiveUpdate = true;
    onRooms(Array.isArray(data?.rooms) ? data.rooms : []);
  };

  const svc: any = websocketService;
  svc.on('public_rooms_updated', onUpdate);

  void fetchPublicRooms().then(rooms => {
    // A live tick already answered this question with fresher data.
    if (cancelled || sawLiveUpdate) return;
    onRooms(rooms);
  });

  return () => {
    cancelled = true;
    svc.off('public_rooms_updated', onUpdate);
  };
}

/* ------------------------------------------------------------------ */
/* Quick Match — three answers, and the truth about each                */
/* ------------------------------------------------------------------ */

/** What the server said. `quickMatchFound` / `quickMatchEmpty` /
    `quickMatchBlocked`, normalised into one shape. */
export type QuickMatchReply =
  | { kind: 'found'; roomCode?: string }
  | { kind: 'empty'; message?: string }
  | { kind: 'blocked'; message?: string };

/** What the UI should therefore do. */
export type QuickMatchIntent =
  | { action: 'join'; roomCode: string }
  | { action: 'open-public'; notice: string }
  | { action: 'explain'; notice: string };

/**
 * Map the server's answer to an action — as pure logic, so the honesty rule is
 * a property that can be PROVEN rather than a thing we remember to do.
 *
 * The rule: an empty pool must never resolve to `join`. There is nothing to
 * join. The temptation in a young product is to paper over an empty pool —
 * seat the player with a bot and call it a match, or show a room that isn't
 * there. That is the fabrication this product has already thrown code away to
 * avoid. An empty pool means we make YOU the room, and we say so.
 */
export function resolveQuickMatch(reply: QuickMatchReply): QuickMatchIntent {
  switch (reply.kind) {
    case 'found':
      // A "match" with no room is not a match. Never navigate to nowhere.
      return reply.roomCode
        ? { action: 'join', roomCode: reply.roomCode }
        : { action: 'explain', notice: 'That game just closed. Try again?' };

    case 'empty':
      return {
        action: 'open-public',
        notice: 'No open games — starting one for you.',
      };

    case 'blocked':
      // The server knows WHY (not signed in / blocked / rate-limited). Prefer
      // its reason over a guess; only fall back if it didn't give one.
      return {
        action: 'explain',
        notice: reply.message || "You can't be matched right now.",
      };
  }
}

/** Games still gathering players are the ones worth showing first — you can
    join a game in progress, but you'll be starting from behind. */
export function sortPublicRooms(rooms: PublicRoom[]): PublicRoom[] {
  return [...rooms].sort((a, b) => {
    const aOpen = a.status !== 'playing';
    const bOpen = b.status !== 'playing';
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    // Then the fullest room that still has a seat — a game with four people in
    // it is more likely to actually start than one with a lone host.
    return b.playerCount - a.playerCount;
  });
}
